import * as Sentry from '@sentry/react';
import type { BrowserOptions } from '@sentry/react';

type SentryReleaseInfo = Pick<BrowserOptions, 'release' | 'environment'>;

// v10 header/query-param scrubbing applied when `sendDefaultPii` was unset (Sentry v10 → v11 migration guide)
const V10_PII_DENYLIST = { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] };

/**
 * Sentry v11 options reproducing the v10 behaviour (ADR 0003): v11 defaults that collect more data,
 * stream spans, attach synthetic stack traces or change sessions and transactions are reverted explicitly.
 */
const buildSentryOptions = ({ release, environment }: SentryReleaseInfo): BrowserOptions => ({
  normalizeDepth: 3,
  tracesSampleRate: 0.1,
  release,
  environment,
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: { request: V10_PII_DENYLIST, response: V10_PII_DENYLIST },
    httpBodies: [],
    urlQueryParams: V10_PII_DENYLIST,
    genAI: { inputs: false, outputs: false },
    databaseQueryData: false,
    queues: false,
    graphQL: { document: false, variables: false },
    frameContextLines: 7,
  },
  traceLifecycle: 'static',
  attachStacktrace: false,
  integrations: [
    Sentry.browserTracingIntegration({ instrumentBfcacheRestore: false }),
    Sentry.browserSessionIntegration({ lifecycle: 'route' }),
    Sentry.userTimingIntegration(),
  ],
  dsn: 'https://9bfdb73dd6f24cd98c9e40fa5ce1700f@o326541.ingest.sentry.io/5228125',
  ignoreErrors: [
    // Random plugins/extensions
    'top.GLOBALS',
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    "Can't find variable: ZiteReader",
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
    // reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    'ResizeObserver loop limit exceeded',
  ],
  denyUrls: [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  ],
});

export default buildSentryOptions;
